import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Plus,
  Trash2,
  X,
  Undo2,
  Redo2,
  Loader2,
  LayoutTemplate,
  Copy,
  Check,
  AlertTriangle,
  GripVertical,
  Search,
  Globe,
  FileDown,
} from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Checkbox } from "../../components/ui/checkbox";
import { Label } from "../../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";

const ANCHOR_OPTIONS = [
  { value: "contract", label: "Sözleşme Tarihi" },
  { value: "setup_start", label: "Montaj Birinci Gün" },
  { value: "fair_start", label: "Fuar Açılış Günü" },
  { value: "fair_end", label: "Fuar Son Gün" },
  { value: "teardown_end", label: "Demontaj Son Gün" },
];
const anchorLabel = (v) =>
  (ANCHOR_OPTIONS.find((o) => o.value === v) || ANCHOR_OPTIONS[2]).label;

const normalizeGroups = (groups) =>
  (groups || []).map((g) => ({
    emoji: g.emoji || "",
    name: g.name,
    tasks: (g.tasks || []).map((x) =>
      typeof x === "string"
        ? { title: x, offset_days: null, saved: true }
        : { title: x.title || "", offset_days: x.offset_days ?? null, saved: true },
    ),
  }));

const isComplete = (t) =>
  !!t.title.trim() && t.offset_days !== null && t.offset_days !== "";

const toPayload = (groups) =>
  groups
    .map((g) => ({
      name: g.name.trim(),
      emoji: g.emoji.trim() || null,
      tasks: g.tasks
        .filter(isComplete)
        .map((t) => ({
          title: t.title.trim(),
          offset_days: t.offset_days,
          anchor: t.anchor || "fair_start",
        })),
    }))
    .filter((g) => g.name);

// A previously-saved task that became incomplete would be DELETED by a save,
// so it blocks auto-save until fixed.
const hasBlockingIncomplete = (groups) =>
  groups.some((g) => g.tasks.some((t) => t.saved && !isComplete(t)));

// Brand-new rows still missing their gün — they don't block the save, they
// are simply excluded until completed.
const hasPendingNew = (groups) =>
  groups.some((g) => g.tasks.some((t) => !t.saved && t.title.trim() && !isComplete(t)));

function SaveStatus({ state }) {
  if (state === "saving")
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-zinc-500" data-testid="expo-tpl-save-status">
        <Loader2 className="h-3 w-3 animate-spin" /> Kaydediliyor…
      </span>
    );
  if (state === "saved")
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600" data-testid="expo-tpl-save-status">
        <Check className="h-3 w-3" /> Otomatik kaydedildi
      </span>
    );
  if (state === "invalid")
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-red-600" data-testid="expo-tpl-save-status">
        <AlertTriangle className="h-3 w-3" /> Kayıtlı bir görevde başlık/gün eksik — düzeltilmeden kaydedilmez
      </span>
    );
  if (state === "partial")
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-600" data-testid="expo-tpl-save-status">
        <AlertTriangle className="h-3 w-3" /> Kaydedildi — gün girilmemiş yeni satırlar bekliyor
      </span>
    );
  if (state === "error")
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-red-600" data-testid="expo-tpl-save-status">
        <AlertTriangle className="h-3 w-3" /> Kaydedilemedi
      </span>
    );
  return (
    <span className="text-[11px] text-zinc-400" data-testid="expo-tpl-save-status">
      Değişiklikler otomatik kaydedilir
    </span>
  );
}

function GroupEditor({ group, gi, onChange, onRequestRemoveGroup, onRequestRemoveTask, onRequestSync, onGroupDragStart, onGroupDragEnd, filterQ }) {
  const setField = (patch) => onChange({ ...group, ...patch });
  const setTask = (ti, patch) => {
    const tasks = [...group.tasks];
    tasks[ti] = { ...tasks[ti], ...patch };
    setField({ tasks });
  };
  // Görevleri grup içinde sürükle-bırak ile sıralama.
  // Çoklu seçim: işaretli satırlar tek sürüklemeyle blok halinde taşınır.
  const dragIndex = useRef(null);
  const [dragOverTi, setDragOverTi] = useState(null);
  const [draggingTi, setDraggingTi] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const taskCount = group.tasks.length;
  useEffect(() => {
    setSelected(new Set());
  }, [taskCount]);
  const toggleSel = (ti) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(ti)) next.delete(ti);
      else next.add(ti);
      return next;
    });
  const moveTask = (from, to) => {
    if (from == null || to == null) return;
    const sel =
      selected.size > 1 && selected.has(from)
        ? [...selected].sort((a, b) => a - b)
        : [from];
    const isSel = new Set(sel);
    if (sel.length === 1 && sel[0] === to) return;
    const tasks = [...group.tasks];
    const moving = sel.map((i) => tasks[i]);
    const rest = tasks.filter((_, i) => !isSel.has(i));
    let insertAt;
    if (isSel.has(to)) {
      insertAt = tasks.slice(0, Math.min(...sel)).filter((_, i) => !isSel.has(i)).length;
    } else {
      const before = tasks.slice(0, to).filter((_, i) => !isSel.has(i)).length;
      insertAt = Math.min(...sel) < to ? before + 1 : before;
    }
    rest.splice(insertAt, 0, ...moving);
    setField({ tasks: rest });
    setSelected(
      sel.length > 1
        ? new Set(moving.map((_, k) => insertAt + k))
        : new Set(),
    );
  };
  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm" data-testid={`expo-tpl-group-${gi}`}>
      <div className="flex items-center gap-2">
        {onGroupDragStart && (
          <span
            draggable
            onDragStart={(e) => {
              e.dataTransfer.effectAllowed = "move";
              const card = e.currentTarget.closest("[data-testid^='expo-tpl-group-']");
              if (card) e.dataTransfer.setDragImage(card, 24, 24);
              onGroupDragStart();
            }}
            onDragEnd={onGroupDragEnd}
            title="Grubu sürükleyerek sırala"
            className="cursor-grab rounded p-1 text-zinc-300 hover:bg-zinc-100 hover:text-zinc-500 active:cursor-grabbing"
            data-testid={`expo-tpl-group-drag-${gi}`}
          >
            <GripVertical className="h-4 w-4" />
          </span>
        )}
        <Input
          value={group.emoji || ""}
          onChange={(e) => setField({ emoji: e.target.value })}
          placeholder="🙂"
          className="w-14 text-center"
          data-testid={`expo-tpl-group-emoji-${gi}`}
        />
        <Input
          value={group.name}
          onChange={(e) => setField({ name: e.target.value })}
          placeholder="Grup adı"
          className="flex-1 font-medium"
          data-testid={`expo-tpl-group-name-${gi}`}
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onRequestRemoveGroup}
          title="Grubu sil"
          className="text-zinc-400 hover:bg-red-50 hover:text-red-600"
          data-testid={`expo-tpl-group-remove-${gi}`}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
      <div className="mt-3 space-y-1.5">
        {selected.size > 0 && (
          <div
            className="flex items-center justify-between rounded-md bg-violet-50 px-2.5 py-1.5 text-[11px] font-medium text-violet-700"
            data-testid={`expo-tpl-selection-bar-${gi}`}
          >
            <span>
              {selected.size} görev seçili — herhangi birini sürükleyin, birlikte taşınır.
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => {
                  const tasks = group.tasks.filter((_, i) => !selected.has(i));
                  setField({ tasks });
                  setSelected(new Set());
                }}
                className="rounded bg-red-50 px-2 py-0.5 font-semibold text-red-600 hover:bg-red-100"
                data-testid={`expo-tpl-selection-delete-${gi}`}
              >
                Seçilenleri sil ({selected.size})
              </button>
              <button
                type="button"
                onClick={() => setSelected(new Set())}
                className="rounded px-1.5 py-0.5 hover:bg-violet-100"
                data-testid={`expo-tpl-selection-clear-${gi}`}
              >
                Temizle
              </button>
            </div>
          </div>
        )}
        {group.tasks.map((task, ti) => {
          const missingOffset =
            task.title.trim() && (task.offset_days === null || task.offset_days === "");
          const canSync = !!task.title.trim() && !missingOffset;
          const hiddenBySearch =
            !!filterQ &&
            !(task.title || "").toLocaleLowerCase("tr").includes(filterQ);
          return (
            <div
              key={ti}
              onDragOver={(e) => {
                if (dragIndex.current == null) return;
                e.preventDefault();
                setDragOverTi(ti);
              }}
              onDragLeave={() => setDragOverTi((v) => (v === ti ? null : v))}
              onDrop={(e) => {
                if (dragIndex.current == null) return;
                e.preventDefault();
                moveTask(dragIndex.current, ti);
                dragIndex.current = null;
                setDragOverTi(null);
              }}
              className={`flex items-center gap-1.5 rounded-md transition-colors ${
                hiddenBySearch ? "hidden" : ""
              } ${
                dragOverTi === ti && dragIndex.current !== ti
                  ? "bg-violet-50 ring-1 ring-violet-300"
                  : selected.has(ti)
                    ? "bg-violet-50/60 ring-1 ring-violet-200"
                    : ""
              } ${
                draggingTi != null && selected.has(draggingTi) && selected.has(ti)
                  ? "opacity-40"
                  : ""
              }`}
            >
              <input
                type="checkbox"
                checked={selected.has(ti)}
                onChange={() => toggleSel(ti)}
                title="Toplu taşıma için seç"
                className="h-3.5 w-3.5 shrink-0 cursor-pointer accent-violet-600"
                data-testid={`expo-tpl-task-select-${gi}-${ti}`}
              />
              <span
                draggable
                onDragStart={(e) => {
                  dragIndex.current = ti;
                  setDraggingTi(ti);
                  e.dataTransfer.effectAllowed = "move";
                  const row = e.currentTarget.parentElement;
                  if (row) e.dataTransfer.setDragImage(row, 16, 16);
                }}
                onDragEnd={() => {
                  dragIndex.current = null;
                  setDraggingTi(null);
                  setDragOverTi(null);
                }}
                title={
                  selected.size > 1 && selected.has(ti)
                    ? `Seçili ${selected.size} görevi birlikte taşı`
                    : "Sürükleyerek sırala"
                }
                className="cursor-grab rounded p-0.5 text-zinc-300 hover:bg-zinc-100 hover:text-zinc-500 active:cursor-grabbing"
                data-testid={`expo-tpl-task-drag-${gi}-${ti}`}
              >
                <GripVertical className="h-4 w-4" />
              </span>
              <span className="w-5 text-right text-[11px] text-zinc-400">{ti + 1}.</span>
              <Input
                value={task.title}
                onChange={(e) => setTask(ti, { title: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    document
                      .querySelector(`[data-testid="expo-tpl-offset-${gi}-${ti}"]`)
                      ?.focus();
                  }
                }}
                placeholder="Görev başlığı"
                className="h-8 flex-1 text-sm"
                data-testid={`expo-tpl-task-${gi}-${ti}`}
              />
              <select
                value={task.anchor || "fair_start"}
                onChange={(e) => setTask(ti, { anchor: e.target.value })}
                title="Gün sayısının referans tarihi (fuar takviminden)"
                className="h-8 w-[136px] shrink-0 rounded-md border border-zinc-200 bg-white px-1 text-[11px] text-zinc-600 focus:outline-none focus:ring-1 focus:ring-violet-300"
                data-testid={`expo-tpl-anchor-${gi}-${ti}`}
              >
                {ANCHOR_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <div
                className="flex items-center gap-1"
                title="Fuar başlangıcına göre gün ofseti (− önce / + sonra) — zorunlu"
              >
                <Input
                  type="number"
                  value={task.offset_days ?? ""}
                  onChange={(e) =>
                    setTask(ti, { offset_days: e.target.value === "" ? null : Number(e.target.value) })
                  }
                  onKeyDown={(e) => {
                    if (e.key !== "Enter") return;
                    e.preventDefault();
                    e.currentTarget.blur();
                  }}
                  placeholder="gün *"
                  className={`h-8 w-20 text-center text-sm ${
                    missingOffset ? "border-amber-400 ring-1 ring-amber-300" : ""
                  }`}
                  data-testid={`expo-tpl-offset-${gi}-${ti}`}
                />
              </div>
              {onRequestSync && (
                <button
                  type="button"
                  onClick={() => onRequestSync(ti)}
                  disabled={!canSync}
                  title={
                    canSync
                      ? "Bu görevi diğer şablonlara da uygula"
                      : "Önce başlık ve gün sayısını doldurun"
                  }
                  className="rounded p-1 text-zinc-400 hover:bg-violet-50 hover:text-violet-600 disabled:cursor-not-allowed disabled:opacity-30"
                  data-testid={`expo-tpl-task-sync-${gi}-${ti}`}
                >
                  <Copy className="h-3.5 w-3.5" />
                </button>
              )}
              <button
                type="button"
                onClick={() => onRequestRemoveTask(ti)}
                title="Görevi sil"
                className="rounded p-1 text-zinc-400 hover:bg-red-50 hover:text-red-600"
                data-testid={`expo-tpl-task-remove-${gi}-${ti}`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          );
        })}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() =>
            setField({
              tasks: [
                ...group.tasks,
                { title: "", offset_days: null, anchor: "fair_start" },
              ],
            })
          }
          className="mt-1 h-7 text-xs text-zinc-500"
          data-testid={`expo-tpl-task-add-${gi}`}
        >
          <Plus className="mr-1 h-3 w-3" /> Görev ekle
        </Button>
      </div>
    </div>
  );
}

export default function ExpoTemplates({ mode = "tenant" }) {
  // "global" → Platform Admin panelindeki tüm-tenant varsayılanları;
  // "tenant" → şirketin kendi şablonları.
  const isGlobal = mode === "global";
  const apiBase = () =>
    isGlobal ? "/admin/expo-templates" : "/expo/templates";
  const [templates, setTemplates] = useState(null);
  const [active, setActive] = useState("usa");
  const [searchQ, setSearchQ] = useState("");
  const [saveStates, setSaveStates] = useState({});
  // Modals
  const [syncTarget, setSyncTarget] = useState(null); // {key, groupName, groupEmoji, task}
  const [syncKeys, setSyncKeys] = useState(new Set());
  const [syncBusy, setSyncBusy] = useState(false);
  const [taskDel, setTaskDel] = useState(null); // {key, gi, ti, task}
  const [taskDelKeys, setTaskDelKeys] = useState(new Set());
  const [taskDelBusy, setTaskDelBusy] = useState(false);
  const [groupDel, setGroupDel] = useState(null); // {key, gi, name}

  const templatesRef = useRef(null);
  templatesRef.current = templates;
  const timersRef = useRef({});

  // Sürükleme sırasında sayfa kenarına yaklaşınca otomatik kaydırma.
  // Native HTML5 DnD'de tarayıcı iç scroll container'ları güvenilir
  // kaydırmadığı için alttaki grubu en üste taşımak imkansızlaşıyordu.
  const pageRef = useRef(null);
  const scrollParentsRef = useRef(null);
  const collectScrollParents = () => {
    const out = [];
    let el = pageRef.current?.parentElement;
    while (el) {
      const st = window.getComputedStyle(el);
      if (/(auto|scroll)/.test(st.overflowY) && el.scrollHeight > el.clientHeight) {
        out.push(el);
      }
      el = el.parentElement;
    }
    out.push(document.scrollingElement || document.documentElement);
    return out;
  };
  const onPageDragOver = (e) => {
    const margin = 130;
    const maxSpeed = 42;
    const h = window.innerHeight;
    let dy = 0;
    if (e.clientY < margin) {
      dy = -Math.ceil(((margin - e.clientY) / margin) * maxSpeed);
    } else if (e.clientY > h - margin) {
      dy = Math.ceil(((e.clientY - (h - margin)) / margin) * maxSpeed);
    }
    if (!dy) return;
    if (!scrollParentsRef.current) scrollParentsRef.current = collectScrollParents();
    scrollParentsRef.current.forEach((s) => s.scrollBy(0, dy));
  };

  // Grupları şablon içinde sürükle-bırak ile sıralama
  const groupDragRef = useRef(null); // { key, gi }
  const [groupDragOver, setGroupDragOver] = useState(null);
  const moveGroup = (key, from, to) => {
    if (from == null || to == null || from === to) return;
    const tpl = (templatesRef.current || []).find((t) => t.key === key);
    if (!tpl) return;
    const groups = [...tpl.groups];
    const [moved] = groups.splice(from, 1);
    groups.splice(to, 0, moved);
    patchGroups(key, groups);
  };

  const setSaveState = (key, state) =>
    setSaveStates((s) => ({ ...s, [key]: state }));

  const load = async () => {
    const { data } = await api.get(apiBase());
    setTemplates(data.map((t) => ({ ...t, groups: normalizeGroups(t.groups) })));
    setSaveStates({});
  };
  useEffect(() => {
    load().catch(() => toast.error("Şablonlar yüklenemedi"));
    const timers = timersRef.current;
    return () => Object.values(timers).forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveNow = async (key, groupsOverride) => {
    clearTimeout(timersRef.current[key]);
    const tpl = (templatesRef.current || []).find((t) => t.key === key);
    const groups = groupsOverride ?? tpl?.groups;
    if (!groups) return false;
    if (hasBlockingIncomplete(groups)) {
      setSaveState(key, "invalid");
      return false;
    }
    setSaveState(key, "saving");
    try {
      await api.put(`${apiBase()}/${key}`, { groups: toPayload(groups) });
      setSaveState(key, hasPendingNew(groups) ? "partial" : "saved");
      // Everything that made it into the payload is now persisted — flag it
      // so future incompleteness on those rows blocks (instead of deleting).
      setTemplates((ts) =>
        ts.map((t) =>
          t.key === key
            ? {
                ...t,
                is_customized: true,
                groups: t.groups.map((g) => ({
                  ...g,
                  tasks: g.tasks.map((x) => (isComplete(x) ? { ...x, saved: true } : x)),
                })),
              }
            : t,
        ),
      );
      return true;
    } catch (err) {
      setSaveState(key, "error");
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Şablon kaydedilemedi",
      );
      return false;
    }
  };

  const scheduleSave = (key) => {
    clearTimeout(timersRef.current[key]);
    const groups = (templatesRef.current || []).find((t) => t.key === key)?.groups || [];
    setSaveState(key, hasBlockingIncomplete(groups) ? "invalid" : "saving");
    timersRef.current[key] = setTimeout(() => saveNow(key), 900);
  };

  // ==== Undo / Redo (oturum içi, şablon başına) ====
  const historyRef = useRef({}); // key -> {past, future, lastTs}
  const [, setHistVersion] = useState(0);
  const histOf = (key) =>
    (historyRef.current[key] ||= { past: [], future: [], lastTs: 0 });

  const applyGroups = (key, groups) => {
    setTemplates((ts) => ts.map((t) => (t.key === key ? { ...t, groups } : t)));
    setTimeout(() => scheduleSave(key), 0);
  };

  const patchGroups = (key, groups) => {
    const prev = (templatesRef.current || []).find((t) => t.key === key)?.groups;
    const h = histOf(key);
    const now = Date.now();
    // Hızlı ardışık değişiklikler (ör. yazma) tek undo adımında birleşir.
    if (prev && now - h.lastTs > 800) {
      h.past.push(prev);
      if (h.past.length > 50) h.past.shift();
    }
    h.future = [];
    h.lastTs = now;
    setHistVersion((v) => v + 1);
    applyGroups(key, groups);
  };

  const undo = (key) => {
    const h = histOf(key);
    if (!h.past.length) return;
    const cur = (templatesRef.current || []).find((t) => t.key === key)?.groups;
    const prev = h.past.pop();
    if (cur) h.future.push(cur);
    h.lastTs = 0;
    setHistVersion((v) => v + 1);
    applyGroups(key, prev);
  };

  const redo = (key) => {
    const h = histOf(key);
    if (!h.future.length) return;
    const cur = (templatesRef.current || []).find((t) => t.key === key)?.groups;
    const next = h.future.pop();
    if (cur) h.past.push(cur);
    h.lastTs = 0;
    setHistVersion((v) => v + 1);
    applyGroups(key, next);
  };

  const [pdfBusy, setPdfBusy] = useState(false);
  const exportPdf = async (tpl) => {
    if (pdfBusy) return;
    setPdfBusy(true);
    try {
      const { data } = await api.get(`${apiBase()}/${tpl.key}/pdf`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${isGlobal ? "global-" : ""}sablon-${tpl.key}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("PDF oluşturulamadı");
    } finally {
      setPdfBusy(false);
    }
  };

  // ==== Cross-template sync (apply task to other templates) ====
  const openSync = (tpl, gi, ti) => {
    const g = tpl.groups[gi];
    setSyncTarget({
      key: tpl.key,
      groupName: g.name,
      groupEmoji: g.emoji,
      task: g.tasks[ti],
    });
    setSyncKeys(new Set());
  };

  const confirmSync = async () => {
    if (!syncTarget || syncKeys.size === 0) return;
    setSyncBusy(true);
    try {
      await saveNow(syncTarget.key);
      const targets = [...syncKeys];
      await api.post("/expo/templates/apply-task", {
        group_name: syncTarget.groupName.trim(),
        group_emoji: syncTarget.groupEmoji?.trim() || null,
        title: syncTarget.task.title.trim(),
        offset_days: syncTarget.task.offset_days,
        anchor: syncTarget.task.anchor || "fair_start",
        target_keys: targets,
      });
      // Mirror the change locally so the item is instantly visible in the
      // target templates without a full reload (keeps unsaved edits intact).
      const gname = syncTarget.groupName.trim().toLocaleLowerCase("tr");
      const title = syncTarget.task.title.trim();
      setTemplates((ts) =>
        ts.map((t) => {
          if (!syncKeys.has(t.key)) return t;
          const groups = t.groups.map((g) => ({ ...g, tasks: [...g.tasks] }));
          let g = groups.find(
            (x) => x.name.trim().toLocaleLowerCase("tr") === gname,
          );
          if (!g) {
            g = { name: syncTarget.groupName.trim(), emoji: syncTarget.groupEmoji || "", tasks: [] };
            groups.push(g);
          }
          if (
            !g.tasks.some(
              (x) => x.title.trim().toLocaleLowerCase("tr") === title.toLocaleLowerCase("tr"),
            )
          ) {
            g.tasks.push({
              title,
              offset_days: syncTarget.task.offset_days,
              anchor: syncTarget.task.anchor || "fair_start",
              saved: true,
            });
          }
          return { ...t, groups, is_customized: true };
        }),
      );
      toast.success(`Görev ${targets.length} şablona uygulandı`);
      setSyncTarget(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Uygulanamadı");
    } finally {
      setSyncBusy(false);
    }
  };

  // ==== Task delete (with optional cross-template removal) ====
  const openTaskDelete = (tpl, gi, ti) => {
    setTaskDel({ key: tpl.key, gi, ti, task: tpl.groups[gi].tasks[ti] });
    setTaskDelKeys(new Set());
  };

  const confirmTaskDelete = async () => {
    if (!taskDel) return;
    setTaskDelBusy(true);
    try {
      const tpl = (templatesRef.current || []).find((t) => t.key === taskDel.key);
      const groups = tpl.groups.map((g, i) =>
        i === taskDel.gi
          ? { ...g, tasks: g.tasks.filter((_, j) => j !== taskDel.ti) }
          : g,
      );
      setTemplates((ts) =>
        ts.map((t) => (t.key === taskDel.key ? { ...t, groups } : t)),
      );
      await saveNow(taskDel.key, groups);
      const title = (taskDel.task.title || "").trim();
      if (taskDelKeys.size > 0 && title) {
        const targets = [...taskDelKeys];
        await api.post("/expo/templates/remove-task", {
          title,
          target_keys: targets,
        });
        const lower = title.toLocaleLowerCase("tr");
        setTemplates((ts) =>
          ts.map((t) => {
            if (!taskDelKeys.has(t.key)) return t;
            return {
              ...t,
              is_customized: true,
              groups: t.groups.map((g) => ({
                ...g,
                tasks: g.tasks.filter(
                  (x) => x.title.trim().toLocaleLowerCase("tr") !== lower,
                ),
              })),
            };
          }),
        );
      }
      toast.success("Görev silindi");
      setTaskDel(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Silinemedi");
    } finally {
      setTaskDelBusy(false);
    }
  };

  // ==== Group delete ====
  const confirmGroupDelete = async () => {
    if (!groupDel) return;
    const tpl = (templatesRef.current || []).find((t) => t.key === groupDel.key);
    const groups = tpl.groups.filter((_, i) => i !== groupDel.gi);
    setTemplates((ts) =>
      ts.map((t) => (t.key === groupDel.key ? { ...t, groups } : t)),
    );
    setGroupDel(null);
    await saveNow(groupDel.key, groups);
    toast.success("Grup silindi");
  };

  if (!templates) {
    return (
      <div className="flex items-center gap-2 p-8 text-sm text-zinc-500" data-testid="expo-templates-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Şablonlar yükleniyor...
      </div>
    );
  }

  const otherTemplates = (key) => templates.filter((t) => t.key !== key);

  // ==== Arama ====
  const searchTerm = searchQ.trim().toLocaleLowerCase("tr");
  const groupVisible = (g) => {
    if (!searchTerm) return true;
    if ((g.name || "").toLocaleLowerCase("tr").includes(searchTerm)) return true;
    return g.tasks.some((t) =>
      (t.title || "").toLocaleLowerCase("tr").includes(searchTerm),
    );
  };
  const matchCount = (tpl) =>
    tpl.groups.reduce(
      (a, g) =>
        a +
        ((g.name || "").toLocaleLowerCase("tr").includes(searchTerm) ? 1 : 0) +
        g.tasks.filter((t) =>
          (t.title || "").toLocaleLowerCase("tr").includes(searchTerm),
        ).length,
      0,
    );

  return (
    <div
      ref={pageRef}
      onDragOver={onPageDragOver}
      className="mx-auto max-w-3xl space-y-6 p-4 sm:p-6"
      data-testid="expo-templates-page"
    >
      <div>
        <h1 className="flex items-center gap-2 text-xl font-semibold text-zinc-900">
          {isGlobal ? (
            <>
              <Globe className="h-5 w-5" /> Global Şablon Yönetimi
            </>
          ) : (
            <>
              <LayoutTemplate className="h-5 w-5" /> Şablon Yönetimi
            </>
          )}
        </h1>
        <p className="mt-1 text-sm text-zinc-500">
          EXPO projelerinde kullanılan To Do List şablonlarının gruplarını ve görev
          taslaklarını düzenleyin. <b>Gün</b> alanı zorunludur — görevin son tarihinin
          fuar başlangıcına göre kaç gün önce (−) veya sonra (+) olacağını belirler.
          Değişiklikler otomatik kaydedilir; yeni eklenen görevler bu şablonu kullanan
          mevcut projelere de anında eklenir, gün değişiklikleri mevcut görevlerin
          terminlerini otomatik günceller.
        </p>
      </div>
      {isGlobal && (
        <div
          className="flex items-start gap-2 rounded-lg border border-violet-200 bg-violet-50 px-3 py-2.5 text-xs leading-relaxed text-violet-800"
          data-testid="expo-tpl-global-banner"
        >
          <Globe className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            <b>Global mod:</b> Burada eklediğiniz yeni grup ve görevler{" "}
            <b>tüm tenant'ların</b> şablonlarına ve bu şablonu kullanan mevcut
            projelerine anında yansır. Tenant'ların kendi yaptığı düzenlemeler
            korunur — silme/yeniden adlandırma yalnızca globali etkiler.
          </span>
        </div>
      )}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
        <Input
          value={searchQ}
          onChange={(e) => setSearchQ(e.target.value)}
          placeholder="Grup veya görev ara…"
          className="pl-9 pr-8"
          data-testid="expo-tpl-search-input"
        />
        {searchQ && (
          <button
            type="button"
            onClick={() => setSearchQ("")}
            title="Aramayı temizle"
            data-testid="expo-tpl-search-clear"
            className="absolute right-2 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <Tabs value={active} onValueChange={setActive}>
        <TabsList className="w-full justify-start overflow-x-auto">
          {templates.map((t) => (
            <TabsTrigger key={t.key} value={t.key} data-testid={`expo-tpl-tab-${t.key}`}>
              {t.label}
              {searchTerm ? (
                <span
                  data-testid={`expo-tpl-tab-matches-${t.key}`}
                  className={`ml-1.5 rounded-full px-1.5 text-[10px] font-semibold ${
                    matchCount(t) > 0
                      ? "bg-amber-100 text-amber-700"
                      : "bg-zinc-100 text-zinc-400"
                  }`}
                >
                  {matchCount(t)}
                </span>
              ) : (
                t.is_customized && <span className="ml-1.5 h-1.5 w-1.5 rounded-full bg-emerald-500" />
              )}
            </TabsTrigger>
          ))}
        </TabsList>
        {templates.map((tpl) => (
          <TabsContent key={tpl.key} value={tpl.key} className="mt-4 space-y-3">
            <div className="flex items-center justify-between">
              <SaveStatus state={saveStates[tpl.key] || "idle"} />
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => exportPdf(tpl)}
                  disabled={pdfBusy}
                  className="h-7 text-xs text-zinc-500"
                  data-testid={`expo-tpl-pdf-${tpl.key}`}
                >
                  <FileDown className="mr-1.5 h-3 w-3" />{" "}
                  {pdfBusy ? "Hazırlanıyor…" : "PDF indir"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => undo(tpl.key)}
                  disabled={!histOf(tpl.key).past.length}
                  title="Geri al"
                  className="h-7 w-7 p-0 text-zinc-500"
                  data-testid={`expo-tpl-undo-${tpl.key}`}
                >
                  <Undo2 className="h-3.5 w-3.5" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => redo(tpl.key)}
                  disabled={!histOf(tpl.key).future.length}
                  title="Yinele"
                  className="h-7 w-7 p-0 text-zinc-500"
                  data-testid={`expo-tpl-redo-${tpl.key}`}
                >
                  <Redo2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
            {tpl.groups.map((g, gi) => (
              <div
                key={gi}
                onDragOver={(e) => {
                  if (groupDragRef.current?.key !== tpl.key) return;
                  e.preventDefault();
                  setGroupDragOver(gi);
                }}
                onDragLeave={() => setGroupDragOver((v) => (v === gi ? null : v))}
                onDrop={(e) => {
                  if (groupDragRef.current?.key !== tpl.key) return;
                  e.preventDefault();
                  moveGroup(tpl.key, groupDragRef.current.gi, gi);
                  groupDragRef.current = null;
                  setGroupDragOver(null);
                }}
                className={`rounded-xl transition-shadow ${
                  !groupVisible(g) ? "hidden" : ""
                } ${
                  groupDragOver === gi && groupDragRef.current?.gi !== gi
                    ? "ring-2 ring-violet-400"
                    : ""
                }`}
              >
                <GroupEditor
                  gi={gi}
                  group={g}
                  filterQ={
                    searchTerm &&
                    !(g.name || "").toLocaleLowerCase("tr").includes(searchTerm)
                      ? searchTerm
                      : ""
                  }
                  onChange={(ng) =>
                    patchGroups(tpl.key, tpl.groups.map((x, i) => (i === gi ? ng : x)))
                  }
                  onRequestRemoveGroup={() => setGroupDel({ key: tpl.key, gi, name: g.name })}
                  onRequestRemoveTask={(ti) => openTaskDelete(tpl, gi, ti)}
                  onRequestSync={
                    isGlobal ? null : (ti) => openSync(tpl, gi, ti)
                  }
                  onGroupDragStart={() => {
                    groupDragRef.current = { key: tpl.key, gi };
                  }}
                  onGroupDragEnd={() => {
                    groupDragRef.current = null;
                    setGroupDragOver(null);
                  }}
                />
              </div>
            ))}
            {searchTerm && tpl.groups.every((g) => !groupVisible(g)) && (
              <div
                className="rounded-xl border border-dashed border-zinc-300 py-10 text-center text-sm text-zinc-400"
                data-testid={`expo-tpl-search-empty-${tpl.key}`}
              >
                "{searchQ.trim()}" ile eşleşen grup veya görev bulunamadı.
              </div>
            )}
            {!searchTerm && (
              <Button
                type="button"
                variant="outline"
                onClick={() =>
                  patchGroups(tpl.key, [...tpl.groups, { emoji: "", name: "", tasks: [] }])
                }
                className="w-full border-dashed"
                data-testid={`expo-tpl-group-add-${tpl.key}`}
              >
                <Plus className="mr-1.5 h-4 w-4" /> Grup ekle
              </Button>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* Apply-to-other-templates modal */}
      <Dialog open={!!syncTarget} onOpenChange={(o) => !o && setSyncTarget(null)}>
        <DialogContent className="sm:max-w-md" data-testid="expo-tpl-sync-dialog">
          <DialogHeader>
            <DialogTitle>Görevi diğer şablonlara uygula</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg bg-zinc-50 px-3 py-2 text-sm">
              <div className="font-medium text-zinc-900">{syncTarget?.task?.title}</div>
              <div className="mt-0.5 text-xs text-zinc-500">
                Grup: {syncTarget?.groupName} · Gün: {syncTarget?.task?.offset_days} ·{" "}
                {anchorLabel(syncTarget?.task?.anchor)}
              </div>
            </div>
            <p className="text-xs text-zinc-500">
              Görev, seçilen şablonlarda aynı isimli gruba eklenir (grup yoksa
              oluşturulur) ve o şablonları kullanan mevcut projelere de anında
              yansır.
            </p>
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => {
                  const others = otherTemplates(syncTarget?.key).map((t) => t.key);
                  setSyncKeys((s) => (s.size === others.length ? new Set() : new Set(others)));
                }}
                className="text-xs font-medium text-violet-600 hover:underline"
                data-testid="expo-tpl-sync-select-all"
              >
                {syncKeys.size === otherTemplates(syncTarget?.key).length
                  ? "Tümünü kaldır"
                  : "Tümünü seç"}
              </button>
              {otherTemplates(syncTarget?.key).map((t) => (
                <label key={t.key} className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={syncKeys.has(t.key)}
                    onCheckedChange={(v) =>
                      setSyncKeys((s) => {
                        const n = new Set(s);
                        if (v) n.add(t.key);
                        else n.delete(t.key);
                        return n;
                      })
                    }
                    data-testid={`expo-tpl-sync-check-${t.key}`}
                  />
                  {t.label}
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setSyncTarget(null)}>Vazgeç</Button>
              <Button
                onClick={confirmSync}
                disabled={syncBusy || syncKeys.size === 0}
                data-testid="expo-tpl-sync-confirm"
              >
                {syncBusy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
                Uygula
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Task delete confirmation modal */}
      <Dialog open={!!taskDel} onOpenChange={(o) => !o && setTaskDel(null)}>
        <DialogContent className="sm:max-w-md" data-testid="expo-tpl-task-delete-dialog">
          <DialogHeader>
            <DialogTitle>Görevi sil</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-zinc-600">
              <b>"{taskDel?.task?.title || "Boş görev"}"</b> bu şablondan silinecek.
              Emin misiniz?
            </p>
            {!!(taskDel?.task?.title || "").trim() && !isGlobal && (
              <div className="space-y-2 rounded-lg border border-zinc-200 p-3">
                <Label className="text-xs text-zinc-500">
                  Diğer şablonlardan da silmek isterseniz seçin:
                </Label>
                {otherTemplates(taskDel?.key).map((t) => (
                  <label key={t.key} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={taskDelKeys.has(t.key)}
                      onCheckedChange={(v) =>
                        setTaskDelKeys((s) => {
                          const n = new Set(s);
                          if (v) n.add(t.key);
                          else n.delete(t.key);
                          return n;
                        })
                      }
                      data-testid={`expo-tpl-task-delete-check-${t.key}`}
                    />
                    {t.label}
                  </label>
                ))}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setTaskDel(null)}>Vazgeç</Button>
              <Button
                variant="destructive"
                onClick={confirmTaskDelete}
                disabled={taskDelBusy}
                data-testid="expo-tpl-task-delete-confirm"
              >
                {taskDelBusy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
                Sil
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Group delete confirmation modal */}
      <Dialog open={!!groupDel} onOpenChange={(o) => !o && setGroupDel(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="expo-tpl-group-delete-dialog">
          <DialogHeader>
            <DialogTitle>Grubu sil</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-zinc-600">
            <b>"{groupDel?.name || "Adsız grup"}"</b> grubu ve içindeki tüm görev
            taslakları bu şablondan silinecek. Emin misiniz?
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setGroupDel(null)}>Vazgeç</Button>
            <Button
              variant="destructive"
              onClick={confirmGroupDelete}
              data-testid="expo-tpl-group-delete-confirm"
            >
              Sil
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
